import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

const makeIdempotencyKey = () => (
  globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const formatInr = (paise) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
}).format((Number(paise) || 0) / 100);

export const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve();
    return;
  }
  const existingScript = document.querySelector('script[data-dektal-razorpay-checkout="true"]');
  if (existingScript) {
    existingScript.addEventListener('load', resolve, { once: true });
    existingScript.addEventListener('error', () => reject(new Error('Razorpay checkout could not be loaded.')), { once: true });
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  script.dataset.dektalRazorpayCheckout = 'true';
  script.onload = resolve;
  script.onerror = () => reject(new Error('Razorpay checkout could not be loaded.'));
  document.body.appendChild(script);
});

export default function useChairPayment({ onVerified } = {}) {
  const onVerifiedRef = useRef(onVerified);
  const [isProcessing, setIsProcessing] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  const startChairPayment = useCallback(async ({ requestedChairCount }) => {
    const requested = Number(requestedChairCount);
    if (!Number.isInteger(requested) || requested < 1) {
      const message = 'Choose a valid total chair count before starting payment.';
      setPaymentError(message);
      toast.error(message);
      return null;
    }

    setIsProcessing(true);
    setPaymentError('');
    let createdOrder = null;
    let checkoutOpened = false;

    const cancelCreatedOrder = async () => {
      if (!createdOrder?.razorpayOrderId) return;
      try {
        await api.post('/addons/marketplace/cash/cancel', {
          razorpay_order_id: createdOrder.razorpayOrderId,
        });
      } catch (_error) {
        // Best effort only. An unverified order cannot create a chair entitlement.
      }
    };

    try {
      createdOrder = (await api.post(
        '/addons/marketplace/additional_chair/cash/order',
        { requestedChairCount: requested },
        { headers: { 'Idempotency-Key': makeIdempotencyKey() } }
      )).data;
      const wrapper = createdOrder;
      const row = wrapper.transaction;
      createdOrder = { ...row, razorpayMode: wrapper.razorpayMode, keyId: wrapper.keyId,
        amountPaise: row.totalCashAmountPaise, paidChairQuantity: row.quantity,
        monthlyRatePaiseSnapshot: row.productSnapshot?.monthlyRatePaise,
        periodStart: row.validityStartAt, periodEnd: row.validityEndAt,
        chairSummary: row.productSnapshot?.chairSummary };
      const checkoutModeLabel = createdOrder.razorpayMode === 'live' ? 'Live' : 'Test';

      const accepted = window.confirm([
        `Included chairs: ${createdOrder.chairSummary?.includedChairs}`,
        `Free/granted active chairs: ${createdOrder.chairSummary?.additionalActive}`,
        `Paid chairs needed: ${createdOrder.paidChairQuantity}`,
        `Monthly rate per paid chair: ${formatInr(createdOrder.monthlyRatePaiseSnapshot)}`,
        `Total payable for one year: ${formatInr(createdOrder.amountPaise)}`,
        `Period: ${new Date(createdOrder.periodStart).toLocaleDateString('en-IN')} to ${new Date(createdOrder.periodEnd).toLocaleDateString('en-IN')}`,
        '',
        `Continue to Razorpay ${checkoutModeLabel} mode checkout?`,
      ].join('\n'));

      if (!accepted) {
        await cancelCreatedOrder();
        setIsProcessing(false);
        return null;
      }

      await loadRazorpayCheckout();
      const checkout = new window.Razorpay({
        key: createdOrder.keyId,
        amount: createdOrder.amountPaise,
        currency: createdOrder.currency,
        order_id: createdOrder.razorpayOrderId,
        name: 'Dektal 360 Pro',
        description: `${createdOrder.paidChairQuantity} Additional Chair${createdOrder.paidChairQuantity === 1 ? '' : 's'} · one year · ${checkoutModeLabel} mode`,
        handler: async (response) => {
          try {
            const verifiedResponse = (await api.post('/addons/marketplace/cash/verify', response)).data;
            const verified = { ...verifiedResponse.transaction, paidChairQuantity: verifiedResponse.transaction?.quantity };
            setReceipt(verified);
            toast.success(`${verified.paidChairQuantity} paid Additional Chair${verified.paidChairQuantity === 1 ? '' : 's'} activated.`);
            await onVerifiedRef.current?.(verified);
          } catch (error) {
            await cancelCreatedOrder();
            const message = error?.response?.data?.error || 'Payment verification failed. No chair was activated.';
            setPaymentError(message);
            toast.error(message);
          } finally {
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: async () => {
            await cancelCreatedOrder();
            toast('Checkout closed. No chair was activated.');
            setIsProcessing(false);
          },
        },
        theme: { color: '#0d9488' },
      });
      checkout.open();
      checkoutOpened = true;
      return createdOrder;
    } catch (error) {
      if (createdOrder && !checkoutOpened) await cancelCreatedOrder();
      const unavailable = error?.response?.data?.code === 'RAZORPAY_NOT_CONFIGURED';
      const message = unavailable
        ? `Payment Unavailable: ${error?.response?.data?.error || 'Razorpay is not configured.'}`
        : error?.response?.data?.error || error.message || 'Could not start Razorpay checkout.';
      setPaymentError(message);
      toast.error(message);
      setIsProcessing(false);
      return null;
    }
  }, []);

  return {
    startChairPayment,
    isProcessing,
    paymentError,
    receipt,
  };
}
